import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Check, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Public pricing page — powers tier selection for practitioners & orgs.
 * Data-driven from GET /api/tiers/catalog so we never drift from server truth.
 */
export default function Pricing() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/tiers/catalog`).then(r => {
      setTiers(r.data.tiers || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const featureLabels = {
    profile_public: "Public profile & services",
    scheduling: "Acuity-style scheduling",
    receive_payments: "Accept payments",
    basic_livestream: "Basic livestream",
    basic_analytics: "Basic analytics",
    crm_notes: "Client CRM (notes, tags)",
    invoices: "Auto-invoices (PDF)",
    courses: "Courses & memberships",
    marketing_tools: "Coupons & marketing",
    advanced_analytics: "Advanced analytics",
    extended_livestream: "Replays & guest speakers",
    priority_placement: "Priority marketplace placement",
    multi_practitioner: "Multiple practitioner seats",
    shared_calendar: "Shared org calendar",
    team_permissions: "Team roles & permissions",
    org_billing: "Recurring org dues",
  };

  const providerTiers = tiers.filter(t => t.audience === "provider" || t.audience === "org");
  const seekerTier = tiers.find(t => t.audience === "client");

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-16" data-testid="pricing-page">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-500/40 text-amber-400 text-xs uppercase tracking-widest mb-4">
            <Sparkles className="h-3 w-3" /> Cauldron plans
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-amber-100 mb-4">
            Grow your sacred practice
          </h1>
          <p className="text-base text-amber-200/70 max-w-2xl mx-auto">
            One platform: booking, payments, courses, community, livestream. Reduced fees on higher plans.
          </p>
        </div>

        {loading ? (
          <div className="text-center text-amber-300/60">Loading tiers…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="pricing-grid">
            {seekerTier && <TierCard tier={seekerTier} featureLabels={featureLabels} />}
            {providerTiers.map(t => (
              <TierCard key={t.id} tier={t} featureLabels={featureLabels} highlighted={t.id === "pro"} />
            ))}
          </div>
        )}

        <p className="text-center text-xs text-amber-300/40 mt-12">
          Fees apply to service bookings. Starter 2.30% · Pro 1.50% · Organization 1.00%.
          Existing $49.99/mo practitioners are automatically upgraded to Pro at no extra cost.
        </p>
      </div>
    </Layout>
  );
}

function TierCard({ tier, featureLabels, highlighted = false }) {
  const trueFeatures = Object.entries(tier.features || {})
    .filter(([, on]) => on)
    .map(([k]) => featureLabels[k])
    .filter(Boolean);

  const priceText = tier.priceUsd === 0
    ? "Free"
    : `$${tier.priceUsd.toFixed(2)}`;

  return (
    <div
      data-testid={`tier-card-${tier.id}`}
      className={`relative rounded-xl border p-6 flex flex-col ${
        highlighted
          ? "border-amber-500/60 bg-gradient-to-b from-amber-950/40 to-neutral-900/80 shadow-lg shadow-amber-500/10"
          : "border-neutral-800 bg-neutral-900/50"
      }`}
    >
      {highlighted && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-neutral-900 text-xs font-semibold px-3 py-1 rounded-full">
          Most popular
        </div>
      )}
      <h3 className="text-lg font-serif text-amber-100 mb-1">{tier.name}</h3>
      <div className="flex items-baseline gap-1 mb-1">
        <span className="text-3xl font-serif text-amber-50">{priceText}</span>
        {tier.priceUsd > 0 && <span className="text-sm text-amber-200/60">/mo</span>}
      </div>
      <div className="text-xs text-amber-200/50 mb-4">
        {tier.feePct}% platform fee on bookings
      </div>
      <ul className="space-y-2 mb-6 text-sm text-amber-100/80">
        {trueFeatures.slice(0, 8).map(f => (
          <li key={f} className="flex items-start gap-2">
            <Check className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div className="mt-auto">
        {tier.audience === "client" ? (
          <Link to="/auth?mode=register">
            <Button variant="outline" className="w-full" data-testid={`tier-cta-${tier.id}`}>
              Sign up free
            </Button>
          </Link>
        ) : (
          <Link to={`/subscribe?tier=${tier.id}`}>
            <Button
              className={`w-full ${highlighted ? "bg-amber-500 hover:bg-amber-400 text-neutral-900" : ""}`}
              data-testid={`tier-cta-${tier.id}`}
            >
              Choose {tier.name}
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
}
